// src/components/Control/ControlPanel/PowerPanel.tsx
// «Питание и сон» в настройках робота: кто на связи и режим сна (fleet_adapter + power.py на Pi).
//   robot/<id>/clients/<user>/<random>  liveliness-токены открытых страниц (useClientPresence)
//   robot/<id>/config  <- {"power": {"sleep", "auto_sleep", "auto_sleep_min"}} (сливается на роботе)
//   robot/<id>/status  -> "power": {"asleep", "reason", "since", "clients", "users"},
//                         "settings.power" (текущие настройки)
// Во сне робот выключает камеры, распознавание людей и предметов, прослушивание;
// остаются связь, статус и режим няни (если включён).
import React from 'react';
import { useRobot, useTelemetryState } from '../robot/RobotContext';
import { PowerSettings, STALE, SystemCmd, fresh } from '../robot/telemetry';
import { putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';

const REASONS: Record<string, string> = {
  manual: 'усыплён с сайта',
  no_clients: 'никого не было на связи',
};

const SYSTEM_TEXT: Record<SystemCmd, [string, string]> = {
  restart_stack: ['Перезапустить ПО робота? Робот остановится и примерно на 30 с пропадёт со связи.',
    'Перезапускаю ПО робота — вернётся примерно через 30 с'],
  reboot: ['Перезагрузить Raspberry Pi? Робот остановится, карта сохранится; связи не будет около минуты.',
    'Перезагружаю Pi — вернётся примерно через минуту'],
  poweroff: ['Выключить Raspberry Pi? Робот остановится, карта сохранится. Включить снова можно только кнопкой питания на Pi или передёрнув питание — с сайта нельзя.',
    'Выключаю Pi. Через ~15 с можно отключать питание'],
};
const PENDING: Record<SystemCmd, string> = {
  restart_stack: 'перезапускается ПО…', reboot: 'перезагружается…', poweroff: 'выключается…',
};

const usersText = (users: Record<string, number>) =>
  Object.entries(users).map(([u, n]) => (n > 1 ? `${u} ×${n}` : u)).join(', ');

const PowerPanel: React.FC = () => {
  useTick(1000);
  const { session, robotId, notify } = useRobot();
  const { clients, power, settings, statusAt, system } = useTelemetryState(t => ({
    clients: t.clients, power: t.status?.power ?? null, settings: t.status?.settings?.power ?? null, statusAt: t.statusAt,
    system: t.status?.system ?? null,
  }));
  const online = fresh(statusAt, STALE.status);
  const supported = online && power != null && settings != null;
  const asleep = supported && power!.asleep;
  const send = (update: Partial<PowerSettings>) => putJson(session, `robot/${robotId}/config`, { power: update });

  const runSystem = (cmd: SystemCmd) => {
    const [question, done] = SYSTEM_TEXT[cmd];
    if (!window.confirm(question)) return;
    putJson(session, `robot/${robotId}/system`, { cmd }).then(ok => notify(ok ? done : 'Не удалось отправить команду', ok ? 'info' : 'error'));
  };

  return (
    <>
      <div className="feature-card">
        <h3><span>Кто на связи</span><span className="badge">{clients.count}</span></h3>
        <p>{clients.count ? `Открытых страниц робота: ${clients.count} — ${usersText(clients.users)}` : 'Нет открытых страниц робота'}</p>
        <p className="hint">Считаются все вкладки с этим роботом, ваша тоже. Закрытая вкладка пропадает из счёта сразу.</p>
      </div>
      <div className={`feature-card power-panel${asleep ? ' asleep' : ''}`}>
        <h3>
          <span>Сон</span>
          {supported && (
            <span className={`power-badge${asleep ? ' asleep' : ''}`}>
              {asleep ? `💤 спит — ${REASONS[power!.reason ?? ''] ?? ''}` : 'работает'}
            </span>
          )}
        </h3>
        {!supported ? (
          <p className="hint">{online ? 'Робот не поддерживает режим сна (обновите ПО на Pi).' : 'Нет статуса робота.'}</p>
        ) : (
          <>
            <div className="power-row">
              {settings!.sleep ? (
                <button type="button" className="op-btn power-wake" onClick={() => send({ sleep: false })}>Разбудить</button>
              ) : (
                <button type="button" className="op-btn" onClick={() => send({ sleep: true })}
                        title="Выключить камеры, распознавание и прослушивание. Связь и режим няни остаются">
                  {asleep ? 'Не будить при открытии страницы' : 'Усыпить'}
                </button>
              )}
            </div>
            <div className="power-row">
              <label className="op-check" title="Когда ни одна страница робота не открыта; проснётся, как только кто-то откроет">
                <input type="checkbox" checked={settings!.auto_sleep}
                       onChange={e => send({ auto_sleep: e.target.checked })} />
                Спать, когда никого нет на связи,
              </label>
              <select value={settings!.auto_sleep_min} disabled={!settings!.auto_sleep}
                      aria-label="Через сколько минут без клиентов засыпать"
                      onChange={e => send({ auto_sleep_min: Number(e.target.value) })}>
                {[1, 5, 15, 30, 60].map(m => <option key={m} value={m}>через {m} мин</option>)}
              </select>
            </div>
            <p className="hint">
              Во сне выключены камеры, распознавание людей и предметов, прослушивание вживую; автономия и поездки
              по карте не запускаются. Работают связь, статус, команды движения и режим няни (если включён).
              Сам робот видит страниц: {power!.clients}. Автосон ждёт конца автономии и снимается, как только
              кто-то открывает страницу робота; ручной — только кнопкой «Разбудить».
            </p>
          </>
        )}
      </div>
      <div className="feature-card">
        <h3>
          <span>Перезапуск и выключение</span>
          {system?.pending && <span className="badge warn">{PENDING[system.pending]}</span>}
        </h3>
        {!online || !system ? (
          <p className="hint">{online ? 'Робот не поддерживает (обновите ПО на Pi).' : 'Нет связи с роботом.'}</p>
        ) : (
          <>
            <div className="row">
              <button type="button" className="op-btn" disabled={!!system.pending} onClick={() => runSystem('restart_stack')}
                      title="Перезапустить ноды ROS (стек fleetcore-robot), Pi не перезагружается">Перезапустить ПО</button>
              <button type="button" className="op-btn" disabled={!!system.pending || !system.can_power} onClick={() => runSystem('reboot')}>
                Перезагрузить Pi
              </button>
              <button type="button" className="op-btn danger" disabled={!!system.pending || !system.can_power} onClick={() => runSystem('poweroff')}>
                Выключить Pi
              </button>
            </div>
            {system.error && <p className="error-text">{system.error}</p>}
            {!system.can_power && (
              <p className="hint">Перезагрузка и выключение недоступны: на Pi не установлено разрешение
                (robot/rpi5/sudoers-fleetcore в /etc/sudoers.d).</p>
            )}
            <p className="hint">Перед действием робот останавливается, автономия выключается, карта сохраняется (кроме перезапуска ПО).
              Pi загружен {new Date(system.boot).toLocaleString()}. После выключения питание можно отключать через ~15 с
              (зелёный светодиод Pi перестал мигать); включить — кнопкой питания на Pi или подачей питания.</p>
          </>
        )}
      </div>
    </>
  );
};

export default PowerPanel;
